"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import { useEventCart } from "@/features/analytics/hooks/use-event-cart";
import { getPricingGtmValue } from "@/features/analytics/lib/gtm-value";
import { useCartStore } from "@/features/cart/components/cart-provider";
import type { CartStore } from "@/features/cart/store/cart-store";
import type { Cart } from "@/features/cart/types/cart.type";
import { ClearCartButton } from "./clear-cart-button";
import { ClearCartDialog } from "./clear-cart-dialog";

export const ClearCartForm = ({
  afterSubmit,
}: {
  afterSubmit?: () => void;
}) => {
  const t = useTranslations();
  const cartStore: CartStore = useCartStore((state) => state);
  const { sendCertEvent } = useEventCart();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const handleClearCart = async () => {
    if (!cartStore.cartId) return;

    setIsPending(true);

    const removedItems = cartStore.items.map((item) => {
      const daysCount = item.configurationItem.dates.length || 1;
      const dailyPrice = item.pricing.final.gross / daysCount;

      return {
        ...item.configurationItem,
        price: dailyPrice,
        quantity: 1,
        duration: daysCount,
        subItemsPricing: item.subItemsPricing,
      };
    });

    await fetch("/api/cart/clear", {
      method: "POST",
      body: JSON.stringify({ cartId: cartStore.cartId }),
    })
      .then(async (res: Response) => {
        if (!res.ok) {
          throw new Error("Failed to create cart");
        }
        const response = res.json() as Promise<{
          cartId: string;
          cart: Cart;
        }>;

        await sendCertEvent({
          eventName: "remove_from_cart",
          diets: removedItems,
          value: getPricingGtmValue({
            pricing: cartStore.pricing,
            items: cartStore.items,
          }),
          other: {
            cartId: cartStore.cartId,
          },
        });

        return response;
      })
      .then(() => {
        cartStore.resetCart();
        cartStore.clearDeliveryCost();
      })
      .then(() => {
        setIsDialogOpen(false);
        afterSubmit?.();
      })
      .catch((error: Error) => toast.error(error.message))
      .finally(() => setIsPending(false));
  };

  const isDisabled = !cartStore.cartId || !cartStore.items?.length;

  return (
    <>
      <ClearCartButton
        className="h-5 px-0"
        disabled={isDisabled}
        onClick={() => setIsDialogOpen(true)}
        type="button"
        variant="link"
      >
        {t("cart.clear")}
      </ClearCartButton>
      <ClearCartDialog
        isPending={isPending}
        onConfirm={handleClearCart}
        onOpenChange={setIsDialogOpen}
        open={isDialogOpen}
      />
    </>
  );
};
